import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Utility } from '@libs/apis';
import { ICustomApplicationsApiOperation } from '../models/ICustomApplicationsApiOperation';
import {
  IAddCollaboratorRequest,
  IBitbucketUser,
  ICollaborator,
  ICreateCustomApplicationRequest,
  ICustomApplicationDetail,
  ICustomApplicationsListResponse,
  INameAvailability,
  IProvisioningStatus
} from '../models/ICustomApplication';

const promiseFrom = lastValueFrom;

export class CustomApplicationsApis implements ICustomApplicationsApiOperation {
  private constructor(
    private readonly httpClient: HttpClient,
    private readonly apiV1: string,
    private readonly apiV2: string
  ) {}

  public static customApplicationsApiOperation(
    httpClient: HttpClient,
    apiV1: string,
    apiV2: string
  ): ICustomApplicationsApiOperation {
    return new CustomApplicationsApis(httpClient, apiV1, apiV2);
  }

  public async getCustomApplications(): Promise<ICustomApplicationsListResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications`
    });
    return promiseFrom(
      this.httpClient.get<ICustomApplicationsListResponse>(url)
    );
  }

  public async getCustomApplication(
    id: number
  ): Promise<ICustomApplicationDetail> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<ICustomApplicationDetail>(url));
  }

  public async createCustomApplication(
    request: ICreateCustomApplicationRequest
  ): Promise<ICustomApplicationDetail> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications`
    });
    return promiseFrom(
      this.httpClient.post<ICustomApplicationDetail>(url, request)
    );
  }

  public async retryProvisioning(
    id: number
  ): Promise<ICustomApplicationDetail> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications/:id/retry`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.post<ICustomApplicationDetail>(url, {}));
  }

  public async deleteApplication(id: number): Promise<void> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.delete<void>(url));
  }

  public async checkNameAvailability(slug: string): Promise<INameAvailability> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications/name-availability`,
      queryParameters: { slug }
    });
    return promiseFrom(this.httpClient.get<INameAvailability>(url));
  }

  public async getProvisioningStatus(): Promise<IProvisioningStatus> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications/provisioning-status`
    });
    return promiseFrom(this.httpClient.get<IProvisioningStatus>(url));
  }

  public async getBitbucketUsers(): Promise<IBitbucketUser[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications/bitbucket-users`
    });
    return promiseFrom(this.httpClient.get<IBitbucketUser[]>(url));
  }

  public async addCollaborator(
    id: number,
    request: IAddCollaboratorRequest
  ): Promise<ICollaborator> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications/:id/collaborators`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.post<ICollaborator>(url, request));
  }

  public async removeCollaborator(
    id: number,
    collaboratorId: number
  ): Promise<void> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/bff/custom-applications/:id/collaborators/:collaboratorId`,
      pathVariables: { id, collaboratorId }
    });
    return promiseFrom(this.httpClient.delete<void>(url));
  }
}
